import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, PenIcon, TrashIcon } from "lucide-react";
import { ReportApi } from "~/api/report-api";
import { ComboBox } from "~/components/common/combo-box";
import { Button } from "~/components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Badge } from "~/components/ui/badge";
import type { ReportResourceDto, ResourceType } from "~/types/api";
import type { FormOption } from "~/types/ui";
import { resourceTypeToString } from "~/utils/report-util";

export default function ReportResources({
  value,
  onChange,
  includedResourceTypes,
}: {
  value: ReportResourceDto[];
  onChange: (val: ReportResourceDto[]) => void;
  includedResourceTypes: ResourceType[];
}) {
  const { t } = useTranslation();
  const [resourceType, setResourceType] = useState<ResourceType | null>(null);
  const [selected, setSelected] = useState<string[]>([]);

  const { data: resources = [] } = useQuery({
    queryKey: ["reportResources:byType", resourceType],
    enabled: resourceType !== null,
    queryFn: async () => {
      const reportApi = new ReportApi();
      const res = await reportApi.getResources(resourceType as ResourceType);
      return res.ok ? (res.data ?? []) : [];
    },
  });

  const resourceOptions: FormOption<string>[] = useMemo(
    () =>
      resources.map(
        (r) =>
          ({
            label: r.name ?? r.identifier,
            value: r.uniqueIdentifier ?? r.identifier,
          }) as FormOption<string>,
      ),
    [resources],
  );

  const resourcesByType = useMemo(() => {
    const groups: { resourceType: ResourceType; resources: ReportResourceDto[] }[] =
      [];
    for (const r of value) {
      const found = groups.find((g) => g.resourceType === r.resourceType);
      if (found) found.resources.push(r);
      else groups.push({ resourceType: r.resourceType as ResourceType, resources: [r] });
    }
    return groups;
  }, [value]);

  function addResources() {
    if (resourceType === null || selected.length === 0) return;

    const toAdd = resources.filter((r) =>
      selected.includes(r.uniqueIdentifier ?? r.identifier),
    );

    const remaining = value.filter((v) => v.resourceType !== resourceType);
    onChange([
      ...remaining,
      ...toAdd.map((r) => ({ ...r, resourceType }) as ReportResourceDto),
    ]);

    setResourceType(null);
    setSelected([]);
  }

  function removeResourceType(type: ResourceType) {
    onChange(value.filter((v) => v.resourceType !== type));
  }

  function editResourceType(type: ResourceType) {
    setResourceType(type);
    setSelected(
      value
        .filter((v) => v.resourceType === type)
        .map((v) => v.uniqueIdentifier ?? v.identifier),
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-center">
        <Select
          value={resourceType !== null ? resourceType.toString() : ""}
          onValueChange={(v) => {
            setResourceType(Number(v) as ResourceType);
            setSelected([]);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("resourceType.select")} />
          </SelectTrigger>
          <SelectContent>
            {includedResourceTypes.map((type) => (
              <SelectItem key={type} value={type.toString()}>
                {resourceTypeToString(type)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {resourceType !== null && (
          <>
            <div className="w-full">
              <ComboBox
                options={resourceOptions}
                value={selected}
                onChange={(v) => setSelected(v as string[])}
                placeholder={t("resourceType.resource")}
                isMulti
                clearable
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setSelected(resourceOptions.map((o) => o.value))}
            >
              <CheckIcon className="h-[1.2rem] w-[1.2rem] text-green-600" />
            </Button>
          </>
        )}

        <Button type="button" variant="outline" onClick={addResources}>
          {t("resourceType.add")}
        </Button>
      </div>

      {resourcesByType.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">{t("resourceType.selected")}</h4>
          {resourcesByType.map((g) => (
            <div
              key={g.resourceType}
              className="flex items-center gap-2 border rounded-md p-2"
            >
              <span className="text-xs uppercase text-muted-foreground w-28">
                {resourceTypeToString(g.resourceType)}
              </span>
              <div className="flex flex-wrap gap-1 flex-1">
                {g.resources.map((r) => (
                  <Badge key={r.identifier + (r.resourceKey ?? "")} variant="outline">
                    {r.name ?? r.identifier}
                  </Badge>
                ))}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => editResourceType(g.resourceType)}
              >
                <PenIcon className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeResourceType(g.resourceType)}
              >
                <TrashIcon className="h-4 w-4" color="red" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
